/* eslint-disable @typescript-eslint/no-unused-vars */
import * as d3 from 'd3'
import tubeMap from '../lib/tube-map/map'
import { positionPopup } from './mapAnimations'

const mapHeight = 1200
const mapWidth = 1500
const svgHeight = window.innerHeight
const svgWidth = window.innerWidth
const xCorrection = 89
const yCorrection = 44

function getTextPos(lineName: any, idx: any) {
  if (typeof idx === 'string') idx = parseInt(idx)
  switch (lineName) {
    case 'Line 1':
      if (idx === 0 || idx === 3 || idx === 8 || idx === 13) return 'W'
      if (idx === 1 || idx === 2 || idx === 6 || idx === 7 || idx === 12) return 'E'
      if (idx === 9) return 'SW'
      if (idx === 4 || idx === 10) return 'NW'
      if (idx === 5) return 'SE'
      if (idx === 15) return 'N'
      return 'SE'
    default:
      return 'SE'
  }
}

// (!isEnd && (id === 0 || id === length - 1)) || (isEnd && id === 1)
function determineStationKind(id: any, length: any, lineCoords: any, start: any, end: any, isEnd = false) {
  switch (true) {
    case lineCoords[id]?.interchange:
      return 'interchange'
    case (start || end || id === 0):
      return 'transfer'
    default:
      return 'normal'
  }
}

export function formatData(lang: any, allData: any, coords: any, localityData: any) {
  const cleanData: any = { stations: {}, lines: [], airports: [], locality: {}, educational_institutes: [], railway: [], market: [], hospital: [], bus: [] }
  Object.entries(allData).forEach(([lineName, { color, stations: st, airports: at, educational_institutes: ei, railway: rl, market: mk, hospital: hp, bus: bs }]: any) => {
    st.forEach((station: any) => {
      let code = station.code
      if (Object.keys(cleanData.stations).includes(station.code)) {
        code = `${station.code}tmp`
      }
      cleanData.stations[code] = {
        id: station.id,
        name: station.code,
        label: station[lang === 'hi' ? 'name_hi' : lang === 'mr' ? 'name_mr' : 'name'],
        coords: coords[lineName][station.id],
      }

      if (Object.keys(localityData)?.length > 0) {
        if (localityData?.from) {
          if (station.code === localityData?.from?.toSt?.code) {
            localityData.from.toSt.x = coords[lineName][station.id]["x"]
            localityData.from.toSt.y = coords[lineName][station.id]["y"]
          }
        }

        if (localityData?.to) {
          if (station.code === localityData?.to?.fromSt?.code) {
            localityData.to.fromSt.x = coords[lineName][station.id]["x"]
            localityData.to.fromSt.y = coords[lineName][station.id]["y"]
          }
        }
      }
    })

    st.forEach((station: any) => {
      if (station.id === 0) return
      const isSplitOnGreen = lineName === 'Line 5' && station.id === 23
      const isJoinOnRapidMetro = lineName === 'Rapid Metro' && station.id === 4
      const isSplitOnRapidMetro = lineName === 'Rapid Metro' && station.id === 5
      let prevStationId = station.id - (isSplitOnGreen ? 3 : 1)
      if (isJoinOnRapidMetro) {
        cleanData.lines.push({
          name: `${station.code}-${st[0].code}`,
          shiftCoords: [0, 0],
          color: `${color}`,
          nodes: [
            {
              coords: Object.values(coords[lineName][station.id]),
              labelPos: getTextPos(lineName, station.id),
              name: station.code,
              marker: determineStationKind(
                station.id,
                st?.length,
                coords[lineName],
                station.is_start,
                station.is_end
              ),
            },
            {
              coords: Object.values(coords[lineName][0]),
              labelPos: getTextPos(lineName, 0),
              name: st[0].code,
              marker: determineStationKind(0, st?.length, coords[lineName], station.is_start, station.is_end),
            },
          ],
        })
      }
      if (isSplitOnRapidMetro) prevStationId = 0
      cleanData.lines.push({
        name: `${station.code}-${st[prevStationId].code}`,
        shiftCoords: [0, 0],
        color: `${color}`,
        nodes: [
          {
            coords: Object.values(coords[lineName][station.id]),
            labelPos: getTextPos(lineName, station.id),
            name: station.code,
            marker: determineStationKind(
              station.id,
              st?.length,
              coords[lineName],
              station.is_start,
              station.is_end
            ),
          },
          {
            coords: Object.values(coords[lineName][prevStationId]),
            labelPos: getTextPos(lineName, prevStationId),
            name: st[prevStationId].code,
            marker: determineStationKind(
              prevStationId,
              st?.length,
              coords[lineName],
              station.is_start,
              station.is_end
            ),
          },
        ],
      })
    })

    at.forEach((airport: any) => {
      let obj = {
        id: airport.id,
        name: airport.code,
        label: airport[lang === 'hi' ? 'name_hi' : lang === 'mr' ? 'name_mr' : 'name'],
        coords: coords[lineName][airport.id],
        x: coords[lineName][airport.id]['x'],
        y: coords[lineName][airport.id]['y']
      }

      cleanData.airports.push(obj)
    })

    ei.forEach((educationalInstitutes: any) => {
      let obj = {
        id: educationalInstitutes.id,
        name: educationalInstitutes.code,
        label: educationalInstitutes[lang === 'hi' ? 'name_hi' : lang === 'mr' ? 'name_mr' : 'name'],
        coords: coords[lineName][educationalInstitutes.id],
        x: coords[lineName][educationalInstitutes.id]['x'],
        y: coords[lineName][educationalInstitutes.id]['y']
      }

      cleanData.educational_institutes.push(obj)
    })

    rl.forEach((railway: any) => {
      let obj = {
        id: railway.id,
        name: railway.code,
        label: railway[lang === 'hi' ? 'name_hi' : lang === 'mr' ? 'name_mr' : 'name'],
        coords: coords[lineName][railway.id],
        x: coords[lineName][railway.id]['x'],
        y: coords[lineName][railway.id]['y']
      }

      cleanData.railway.push(obj)
    })

    mk.forEach((market: any) => {
      let obj = {
        id: market.id,
        name: market.code,
        label: market[lang === 'hi' ? 'name_hi' : lang === 'mr' ? 'name_mr' : 'name'],
        coords: coords[lineName][market.id],
        x: coords[lineName][market.id]['x'],
        y: coords[lineName][market.id]['y']
      }

      cleanData.market.push(obj)
    })

    hp.forEach((hospital: any) => {
      let obj = {
        id: hospital.id,
        name: hospital.code,
        label: hospital[lang === 'hi' ? 'name_hi' : lang === 'mr' ? 'name_mr' : 'name'],
        coords: coords[lineName][hospital.id],
        x: coords[lineName][hospital.id]['x'],
        y: coords[lineName][hospital.id]['y']
      }

      cleanData.hospital.push(obj)
    })

    bs.forEach((bus: any) => {
      let obj = {
        id: bus.id,
        name: bus.code,
        label: bus[lang === 'hi' ? 'name_hi' : lang === 'mr' ? 'name_mr' : 'name'],
        coords: coords[lineName][bus.id],
        x: coords[lineName][bus.id]['x'],
        y: coords[lineName][bus.id]['y']
      }

      cleanData.bus.push(obj)
    })

    cleanData.locality = localityData
  })
  Object.keys(cleanData.stations).forEach((key) => {
    if (key.endsWith('tmp')) {
      delete cleanData.stations[key]
    }
  })

  return cleanData
}

export function drawMap(data: any, showPopup: any) {
  let availableHeight = window.screen.availHeight;
  let currentZoom = .85
  if (availableHeight > 3000) {
    currentZoom = 1.4
  }
  const maxZoom = 1.4
  const scaleFactor = 0.2

  let startX = 0
  let startY = 0
  document.querySelector('#map svg')?.remove()
  const map: any = tubeMap()

  let originalPosition = window.innerWidth < 550 ? { x: -250, y: 0 } : window.innerWidth < 815 ? { x: -100, y: 0 } : window.innerWidth < 900 ? { x: -250, y: 0 } : window.innerWidth < 1024 ? { x: -150, y: 0 } : window.innerWidth < 1280 ? { x: -100, y: 0 } : { x: 0, y: 0 }; // Store the original position of the element

  const container = d3.select('#map')
  const initMap = (data: any) => {
    map
      .width(mapWidth)
      .height(mapHeight)
      .on('click', (name: any) => {
        if (['path', 'IMG', 'rect'].includes(d3?.event?.target?.tagName)) {
          showPopup(name, currentZoom)
        }
      })
    container.datum(data).call(map)

    const svg = container.select('svg')

    const svgBox = (document
      .getElementById('map') as any)
      .getElementsByTagName('svg')[0]
      .getBoundingClientRect()

    const groupBBox = (document
      .getElementById('map') as any)
      .getElementsByTagName('svg')[0]
      .getElementsByTagName('g')[0]
      .getBBox()

    /* const centerGroup = (d: any) =>
      `translate(${(d.x =
        xCorrection +
        svgBox.width / 2 -
        (groupBBox.width * currentZoom) / 2)}, ${(d.y =
          yCorrection +
          svgBox.height / 2 -
          (groupBBox.height * currentZoom) / 2)}) scale(${currentZoom})` */

    const centerGroup = (d: any) =>
      `translate(${(d.x =
        originalPosition.x)}, ${(d.y =
          originalPosition.y)}) scale(${currentZoom})`

    // const centerGroup = (d: any) =>
    //   `translate(${(d.x =
    //     xCorrection + 320)}, 0) scale(${currentZoom + 0.175})`

    const g = svg
      .select('g')
      .attr('id', 'tube-map')
      .attr('height', svgHeight)
      .attr('width', svgWidth)
      .attr('transform', centerGroup)

    const updateFlags = () =>
      g
        .selectAll('.fromFlags div, .toFlags div')
        .style('transform', `scale(${1 / currentZoom}`)
        .style('transform-origin', 'bottom')

    const drag = d3
      .drag()
      .on('start', () => {
        startX = d3.event.x
        startY = d3.event.y
      })
      .on('drag', (d: any) => {
        g.attr(
          'transform',
          `translate(${(d.x = d3.event.x)}, ${(d.y =
            d3.event.y)}) scale(${currentZoom})`
        )
        positionPopup([d3.event.x - startX, d3.event.y - startY], true)
        startX = d3.event.x
        startY = d3.event.y
      })
      .on('end', (d: any) => {
        // originalPosition = window.innerWidth < 768 ? { x: -250, y: 0 } : { x: 0, y: 0 }
        // g.attr(
        //   'transform',
        //   `translate(${(d.x = originalPosition.x)}, ${(d.y = originalPosition.y)}) scale(${currentZoom})`
        // );
        positionPopup([0, 0], false);
      })

    svg.call(drag as any)


    return Object.assign(g.node() as any, {
      zoomIn: () => {

        if (currentZoom < maxZoom) currentZoom += scaleFactor
        g.transition().attr('transform', centerGroup)
        updateFlags()
      },
      zoomOut: () => {
        if (currentZoom > 1) currentZoom -= scaleFactor
        g.transition().attr('transform', centerGroup)
        updateFlags()
      },
    })
  }
  return initMap(data)
}
